import { createContext, useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { requestRefresh, setAccessToken, setAuthFailureHandler } from '@/lib/axios';
import { loginRequest, logoutRequest } from './api';
import type { AuthUser } from './types';

type AuthStatus = 'loading' | 'authenticated' | 'unauthenticated';

export interface AuthContextValue {
  user: AuthUser | null;
  status: AuthStatus;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  /** Vrai si l'utilisateur possède TOUTES les permissions demandées. */
  hasPermission: (...perms: string[]) => boolean;
}

// eslint-disable-next-line react-refresh/only-export-components
export const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [status, setStatus] = useState<AuthStatus>('loading');
  const bootstrapped = useRef(false);

  // Restauration silencieuse de la session au démarrage (via cookie de refresh).
  useEffect(() => {
    if (bootstrapped.current) return;
    bootstrapped.current = true;

    requestRefresh()
      .then((res) => {
        setUser(res.user);
        setStatus('authenticated');
      })
      .catch(() => {
        setStatus('unauthenticated');
      });
  }, []);

  // Si un refresh échoue en cours de session, on repasse en non-authentifié.
  useEffect(() => {
    setAuthFailureHandler(() => {
      setUser(null);
      setStatus('unauthenticated');
    });
    return () => setAuthFailureHandler(null);
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const res = await loginRequest({ email, password });
    setUser(res.user);
    setStatus('authenticated');
  }, []);

  const logout = useCallback(async () => {
    await logoutRequest();
    setAccessToken(null);
    setUser(null);
    setStatus('unauthenticated');
  }, []);

  const hasPermission = useCallback(
    (...perms: string[]) => {
      if (!user) return false;
      const granted = new Set(user.permissions);
      return perms.every((p) => granted.has(p));
    },
    [user],
  );

  return (
    <AuthContext.Provider value={{ user, status, login, logout, hasPermission }}>
      {children}
    </AuthContext.Provider>
  );
}
